import { useQueryClient } from "@tanstack/react-query";
import useOffsetCodeConfigs from "features/fees/offsets/useOffsetCodeConfigs";
import {
  getFeeChargeEventBasisChangeByUseRetrieveQueryKey,
  getFeeChargeEventListQueryKey,
  useFeeChargeEventRetrieveSuspense,
} from "gen/endpoints/fee-charge-event/fee-charge-event";
import { useFundFamilyEventRetrieve } from "gen/endpoints/fund-family-event/fund-family-event";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FullScreenLoadingState } from "shared/components/empty/FullScreenLoadingState";
import {
  PageWizardProvider,
  PageWizardRoutes,
} from "shared/components/layout/PageWizard";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute, RouteId } from "shared/navigation/Routes";

export const FeePostingPage: React.FC = () => {
  const { t } = useTranslation(["fees"]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { fundFamilyId, feeChargeEventId } = useRequiredStringParams();
  const { data: feeChargeEventData } = useFeeChargeEventRetrieveSuspense(
    fundFamilyId,
    feeChargeEventId,
  );

  const { data: fundFamilyEventData } = useFundFamilyEventRetrieve(
    fundFamilyId,
    feeChargeEventData.fee_charge_event.fund_family_event_id!,
    {
      query: {
        enabled: !!feeChargeEventData.fee_charge_event.fund_family_event_id,
        refetchInterval: 1000,
      },
    },
  );

  const celery_running = fundFamilyEventData?.event_workflow_status.is_running;

  useEffect(() => {
    void (async () => {
      await queryClient.invalidateQueries({
        queryKey: getFeeChargeEventBasisChangeByUseRetrieveQueryKey(
          fundFamilyId,
          feeChargeEventId,
          {
            is_equalized_view: false,
            include_draft_transactions: false,
          },
        ),
      });
    })();
  }, [fundFamilyId, feeChargeEventId, celery_running, queryClient]);

  const { data: offsetCodeConfigs } = useOffsetCodeConfigs(fundFamilyId);

  const hiddenPages: Partial<Record<RouteId, boolean>> = {
    feesPostApplyOffsets: !offsetCodeConfigs?.length,
  };

  const navigateToFeesPage = () => {
    navigate(
      rootRoute({}).fundFamilies({}).fundFamily({ fundFamilyId }).fees({}).$,
    );
  };

  const onFinish = async () => {
    await queryClient.invalidateQueries({
      queryKey: getFeeChargeEventListQueryKey(fundFamilyId),
    });
    navigateToFeesPage();
  };

  return (
    <>
      {celery_running && (
        <FullScreenLoadingState
          message={fundFamilyEventData.event_workflow_status.message}
        />
      )}
      <PageWizardProvider
        allowNavigate={false}
        finishTitle={t("fees:postFees.postFees")}
        onFinish={onFinish}
        onExit={navigateToFeesPage}
        hiddenPages={hiddenPages}
      >
        <PageWizardRoutes />
      </PageWizardProvider>
    </>
  );
};
